import React from "react";
//#region types
import { FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
//#endregion
//#region components
import { useCustomerSearch } from "components/customer";
import { useCompanyService, useCustomerService, useRecurringInvoiceService } from "serviceHooks";
//#endregion
import { EnumHelper, FilterHelper, LocalizationHelperInstance, QueryStateHelper } from "utils/helpers";

function useRecurringInvoiceFilterDefinition() {
  const companyService = useCompanyService();
  const customerService = useCustomerService();
  const recurringInvoiceService = useRecurringInvoiceService();

  const [suggestedCustomers, setSuggestedCustomers] = React.useState<AutocompleteListItem[]>([]);
  const searchCustomers = useCustomerSearch();

  React.useEffect(() => {
    customerService
      .getCustomerSuggestions()
      .then((res) =>
        setSuggestedCustomers(res.map((customer: any) => ({ id: customer.id ?? 0, value: customer.name ?? "" })))
      );
  }, []);

  const apiToReportFilterFormatter = async (
    filter: WintSuperkollAPIModel.RecurringInvoiceFilter
  ): Promise<InvoiceFormTypes.RecurringInvoiceFilter> => {
    const reportFilter: InvoiceFormTypes.RecurringInvoiceFilter = {};

    let ownerName: Promise<string> | undefined;
    if (filter.ownerIds?.length) {
      reportFilter.ownerId = filter.ownerIds[0];
      ownerName = companyService.getEmployeeName(filter.ownerIds[0]);
    }

    let customerName: Promise<string> | undefined;
    if (filter.customerIds?.length) {
      reportFilter.customerId = filter.customerIds[0];
      customerName = customerService.getCustomer(reportFilter.customerId).then((c) => c.name ?? "");
    }

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Invoice.RecurringInvoiceFilter.StartDate,
      filter.firstInvoiceDateFrom,
      filter.firstInvoiceDateTo
    );

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Invoice.RecurringInvoiceFilter.EndDate,
      filter.endDateFrom,
      filter.endDateTo
    );

    if (filter.recurringInvoiceSendType?.length) {
      reportFilter.sendType = recurringInvoiceService
        .getSendTypes()
        .filter((s) => filter.recurringInvoiceSendType?.includes(s.id as number)) as SelectionItem[];
    }

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Invoice.RecurringInvoiceFilter.NumOfSentInvoices,
      filter.numberOfSentInvoicesFrom,
      filter.numberOfSentInvoicesTo
    );

    if (filter.frequencies?.length) {
      reportFilter.frequency = recurringInvoiceService
        .getStates()
        .filter((s) => filter.frequencies?.includes(s.id as number)) as SelectionItem[];
    }

    if (filter.states?.length) {
      reportFilter.status = recurringInvoiceService
        .getStates()
        .filter((s) => filter.states?.includes(s.id as number)) as SelectionItem[];
    }

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Invoice.RecurringInvoiceFilter.Amount,
      filter.minTotalAmount,
      filter.maxTotalAmount
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Invoice.RecurringInvoiceFilter.Income,
      filter.totalIncomeFrom,
      filter.totalIncomeTo
    );

    if (ownerName) {
      reportFilter.ownerName = await ownerName;
    }

    if (customerName) {
      reportFilter.customerName = await customerName;
    }

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<InvoiceFormTypes.RecurringInvoiceFilter>[] = [
    {
      label: TranslationKeys.Common.Dates,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        startDate: {
          label: TranslationKeys.Invoice.FirstInvoice,
          type: FormEnums.FormFieldType.DATERANGE,
        },
        endDate: {
          label: TranslationKeys.Invoice.LastInvoice,
          type: FormEnums.FormFieldType.DATERANGE,
        },
      },
    },
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        ownerName: {
          label: TranslationKeys.Common.Owner,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.Invoice.RecurringInvoiceFilter.OwnerId,
            items: (searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false),
          } as AutoCompleteExtraProps<InvoiceFormTypes.RecurringInvoiceFilter>,
        },
        customerName: {
          label: TranslationKeys.Invoice.Customer,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.Invoice.RecurringInvoiceFilter.CustomerId,
            suggestedItems: suggestedCustomers,
            items: searchCustomers,
          } as AutoCompleteExtraProps<InvoiceFormTypes.RecurringInvoiceFilter>,
        },
        sendType: {
          label: TranslationKeys.Invoice.ManageRecurring,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: recurringInvoiceService.getSendTypes(),
          },
        },
        status: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: recurringInvoiceService.getStates(),
          },
        },
        frequency: {
          label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HowOften),
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.RecurringInvoiceFrequency).map((key) => ({
              id: key,
              value: LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringFrequency_${key}`),
            })),
          },
        },
        numOfSentInvoices: {
          label: TranslationKeys.Invoice.NumOfInvoices,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
      },
    },

    {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        amount: {
          label: TranslationKeys.Common.Amount,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
          extraProps: {
            allowNegative: true,
          },
        },
        income: {
          label: TranslationKeys.Common.TotalRevenue,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
          extraProps: {
            allowNegative: true,
          },
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<InvoiceFormTypes.RecurringInvoiceFilter> = {
    ownerName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Invoice.RecurringInvoiceFilter.OwnerName),
    ownerId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Invoice.RecurringInvoiceFilter.OwnerId),
    customerName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Invoice.RecurringInvoiceFilter.CustomerName),
    customerId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Invoice.RecurringInvoiceFilter.CustomerId),
    startDate: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.Invoice.RecurringInvoiceFilter.StartDate),
    endDate: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.Invoice.RecurringInvoiceFilter.EndDate),
    sendType: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Invoice.RecurringInvoiceFilter.SendType,
      EnumHelper.getNumericItemValues(WintSuperkollAPIModel.RecurringInvoiceSendType).map((key) => {
        return {
          id: Number(key),
          value: LocalizationHelperInstance.getFormattedMessage(`Invoice.RecurringSendType_${key}`),
        };
      })
    ),
    numOfSentInvoices: QueryStateHelper.numberRangeQueryEncoder(
      FormFieldConstants.Invoice.RecurringInvoiceFilter.NumOfSentInvoices
    ),
    frequency: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Invoice.RecurringInvoiceFilter.Frequency,
      EnumHelper.getNumericItemValues(WintSuperkollAPIModel.RecurringInvoiceFrequency).map((key) => {
        return {
          id: Number(key),
          value: recurringInvoiceService.getFrequencyText(key),
        };
      })
    ),
    status: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Invoice.RecurringInvoiceFilter.Status,
      EnumHelper.getNumericItemValues(WintSuperkollAPIModel.RecurringInvoiceState).map((key) => {
        return {
          id: Number(key),
          value: recurringInvoiceService.getStateText(key),
        };
      })
    ),
    amount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Invoice.RecurringInvoiceFilter.Amount),
    income: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Invoice.RecurringInvoiceFilter.Income),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useRecurringInvoiceFilterDefinition;
